/* KINGDOM MINDED - FRAME GUARD  (2026-09-22)
   The visible garment, not the PNG canvas, is what must stay the same size. Server-side
   normalisation does the real work: every product image is placed from its MEASURED garment
   bounds (product metafield kml.frames: "m<mediaId>" for an image whose bounds differ from its
   view, else the per-view bounds). This guard is the safety net for media that arrives LATER
   without verified bounds:
     - data-kmfr="none"  : no bounds at all. The image stays invisible until its garment has been
                           measured here and placed on the same stage rule, so a raw PNG is never
                           shown at an arbitrary size.
     - data-kmfr="view"  : per-view bounds were used. The image is measured once it loads; if its
                           garment does not fill the crop the way verified media does (a new colour
                           from a different mockup generator), it is re-placed and a console warning
                           names the image so its bounds can be added to kml.frames.
     - data-kmfr="media" : per-image bounds, verified - trusted, not measured.
   QA: add ?kmqa=1 to any shop / category / witness / product URL, then run
   KMFrameGuard.report() in the console: one row per visible garment with its measured occupancy of
   the stage, and every image more than 3 points off its garment-class median flagged OUTLIER. */
(function () {
  'use strict';
  var QA = /[?&]kmqa=1\b/.test(location.search), warned = [];
  function bbox(im) {
    if (!im || !im.complete || !im.naturalWidth) return null;
    var w = 120, h = Math.max(1, Math.round(im.naturalHeight * w / im.naturalWidth));
    var c = document.createElement('canvas'); c.width = w; c.height = h;
    var x = c.getContext('2d', { willReadFrequently: true }), d;
    try { x.drawImage(im, 0, 0, w, h); d = x.getImageData(0, 0, w, h).data; } catch (e) { return TAINT; }
    var l = w, r = -1, t = h, b = -1, px, py;
    for (py = 0; py < h; py++) for (px = 0; px < w; px++) {
      if (d[(py * w + px) * 4 + 3] > 24) { if (px < l) l = px; if (px > r) r = px; if (py < t) t = py; if (py > b) b = py; }
    }
    if (r < 0) return null;
    return { l: l / w, r: (r + 1) / w, t: t / h, b: (b + 1) / h };
  }
  /* The page and its image CDN can be different origins (custom domain, editor preview): the
     canvas is then tainted and cannot be read. The CDN serves Access-Control-Allow-Origin: *, so
     the same URL is measured once from a CORS copy instead - the image never shows unmeasured. */
  var TAINT = { taint: 1 }, cors = {};
  function measure(im, cb) {
    var b = bbox(im); if (b !== TAINT) { cb(b); return; }
    var u = im.currentSrc || im.src, c = cors[u];
    if (c && c.b !== undefined) { cb(c.b); return; }
    if (c) { c.q.push(cb); return; }
    c = cors[u] = { q: [cb] };
    var k = new Image(); k.crossOrigin = 'anonymous';
    k.onload = function () { var r = bbox(k); c.b = r === TAINT ? null : r; c.q.forEach(function (f) { f(c.b); }); c.q = []; };
    k.onerror = function () { c.b = null; c.q.forEach(function (f) { f(null); }); c.q = []; };
    k.src = u;
  }
  function done(el) { el.classList.add('km-fg-ok'); }
  function warn(kind, im) { warned.push([kind, im.getAttribute('alt'), im.getAttribute('data-mid')]); if (window.console) console.warn('[KM frame guard] ' + kind + ':', im.getAttribute('alt'), 'media', im.getAttribute('data-mid'), '- add its bounds to kml.frames'); }
  /* CARD (engine): the CDN crop should hold the garment edge to edge (1% pad). */
  function card(im, b) {
    var mode = im.getAttribute('data-kmfr');
    if (!b) { done(im); return; }
    var off = Math.max(b.t, 1 - b.b), cx = (b.l + b.r) / 2;
    if (mode !== 'none' && off < 0.035 && Math.abs(cx - 0.5) < 0.02) { done(im); return; }
    var st = im.offsetParent, H = st && st.clientHeight; if (!H) { done(im); return; }
    var cs = getComputedStyle(im), curH = parseFloat(cs.height), curT = parseFloat(cs.top);
    var gh = b.b - b.t, nH = curH * 0.978 / gh, nT = curT + curH * 0.011 - nH * b.t;
    im.style.height = (nH / H * 100).toFixed(2) + '%';
    im.style.top = (nT / H * 100).toFixed(2) + '%';
    im.style.transform = 'translate3d(-' + (cx * 100).toFixed(2) + '%,0,0)';
    done(im); warn(mode === 'none' ? 'card image had no bounds' : 'card image bounds were wrong', im);
  }
  /* PRODUCT PAGE: the figure carries the stage rule (data-hf = garment height share of the stage)
     and the bounds it was placed with (data-kb). */
  function pdp(fig, b) {
    var im = fig.querySelector('img'), mode = fig.getAttribute('data-kmfr');
    if (!b) { if (mode === 'none') fig.removeAttribute('data-kmfr'); done(fig); return; }
    var kb = (fig.getAttribute('data-kb') || '').split(',').map(Number);
    if (mode !== 'none' && kb.length === 4) {
      var dv = Math.max(Math.abs(kb[0] / 100 - b.l), Math.abs(kb[1] / 100 - b.r), Math.abs(kb[2] / 100 - b.t), Math.abs(kb[3] / 100 - b.b));
      if (dv < 0.02) { done(fig); return; }
    }
    var st = fig.parentElement, SW = st.clientWidth || 3, SH = st.clientHeight || 4;
    var gh = b.b - b.t, ratio = ((b.r - b.l) * im.naturalWidth) / (gh * im.naturalHeight);
    var hf = parseFloat(fig.getAttribute('data-hf')) || 0.82, cap = 0.94 * (SW / SH) / ratio;
    if (cap < hf) hf = cap;
    fig.style.setProperty('--kh', (hf / gh * 100).toFixed(2) + '%');
    fig.style.setProperty('--kt', (((1 - hf) / 2 - hf * b.t / gh) * 100).toFixed(2) + '%');
    fig.style.setProperty('--kx', ((b.l + b.r) / 2 * 100).toFixed(2));
    fig.setAttribute('data-kmfr', 'guard');
    thumbs();
    done(fig); warn(mode === 'none' ? 'product image had no bounds' : 'product image bounds were wrong', im);
  }
  /* LEGACY single-image cards (/collections/all feature cards, search results): no server bounds
     exist on these templates, so the garment is measured here and placed on one rule - garment
     86% of the frame height (or 92% of its width if wider), centred on the garment itself. They
     stay hidden until placed (CSS in snippets/km-launch-cleanup.liquid). Model photography (no
     transparent margin) is shown as is. */
  var LEG = '.kmc .product-card-media img, .km-card__media .km-card__img';
  function legacy(im, b) {
    if (!b || (b.r - b.l > 0.97 && b.b - b.t > 0.97)) { done(im); return; }
    im.style.objectFit = 'contain'; im.style.objectPosition = '50% 50%'; im.style.padding = '0'; im.style.transformOrigin = '50% 50%';
    var W = im.clientWidth, H = im.clientHeight; if (!W || !H) { done(im); return; }
    var k = Math.min(W / im.naturalWidth, H / im.naturalHeight), dw = im.naturalWidth * k, dh = im.naturalHeight * k;
    var gw = dw * (b.r - b.l), gh = dh * (b.b - b.t), s = Math.min(0.86 * H / gh, 0.92 * W / gw);
    var cx = (W - dw) / 2 + dw * (b.l + b.r) / 2 - W / 2, cy = (H - dh) / 2 + dh * (b.t + b.b) / 2 - H / 2;
    im.style.transform = 'translate(' + (-s * cx / W * 100).toFixed(2) + '%,' + (-s * cy / H * 100).toFixed(2) + '%) scale(' + s.toFixed(4) + ')';
    done(im);
  }
  function handle(im) {
    if (!im || im.tagName !== 'IMG' || !im.complete || !im.naturalWidth) return;
    if (im.hasAttribute('data-kmfr')) {
      if (im.getAttribute('data-kmfr') === 'media' && !QA) { done(im); return; }
      measure(im, function (b) { card(im, b); }); return;
    }
    var fig = im.closest && im.closest('figure[data-kmfr]');
    if (fig) {
      if (fig.getAttribute('data-kmfr') === 'media' && !QA) { done(fig); return; }
      measure(im, function (b) { pdp(fig, b); }); return;
    }
    if (im.matches && im.matches(LEG)) measure(im, function (b) { legacy(im, b); });
  }
  /* PRODUCT THUMBNAILS: each thumb shows the same image as the frame with the same index, on the
     same 3:4 shape, so it takes that frame's exact garment placement (--kh/--kt/--kx). Copied
     before the thumbs paint (this script runs before DOMContentLoaded), and again whenever the
     guard has to re-place a frame. */
  function thumbs() {
    [].forEach.call(document.querySelectorAll('[data-km-thumb]'), function (t) {
      var fig = document.querySelector('[data-km-frame="' + t.getAttribute('data-km-thumb') + '"]');
      if (fig && fig.style.getPropertyValue('--kh')) {
        ['--kh', '--kt', '--kx'].forEach(function (p) { t.style.setProperty(p, fig.style.getPropertyValue(p)); });
        t.setAttribute('data-kn', '1');
      }
      done(t);
    });
  }
  function pass() { thumbs(); [].forEach.call(document.querySelectorAll('img[data-kmfr], figure[data-kmfr] img, ' + LEG), handle); }
  thumbs();
  document.addEventListener('load', function (e) { handle(e.target); }, true);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', pass); else pass();
  window.addEventListener('load', pass);
  var rt; window.addEventListener('resize', function () { clearTimeout(rt); rt = setTimeout(function () { [].forEach.call(document.querySelectorAll(LEG), function (i) { if (i.complete && i.naturalWidth) measure(i, function (b) { legacy(i, b); }); }); }, 200); });
  function report() {
    var rows = [];
    [].forEach.call(document.querySelectorAll('.kme-card img.kme-frame.is-on, figure.km-product__frame.is-active img'), function (im) {
      var box = im.closest('.kme-card__stage, .km-product__stage'); if (!box || !im.naturalWidth) return;
      var b = bbox(im); if (b === TAINT) { var cc = cors[im.currentSrc || im.src]; b = cc && cc.b; } if (!b) return;
      var r = im.getBoundingClientRect(), s = box.getBoundingClientRect(); if (!s.height) return;
      var card = im.closest('.kme-card'), fig = im.closest('figure');
      rows.push({ where: card ? 'card' : 'product', cls: card ? (card.getAttribute('data-fit') === 'w' ? 'womens' : card.getAttribute('data-garment')) : '', view: im.getAttribute('data-view') || '', img: (im.getAttribute('alt') || '').slice(0, 60), bounds: im.getAttribute('data-kmfr') || (fig && fig.getAttribute('data-kmfr')) || '',
        heightPct: +(((b.b - b.t) * r.height) / s.height * 100).toFixed(1), widthPct: +(((b.r - b.l) * r.width) / s.width * 100).toFixed(1),
        centreYPct: +(((r.top + (b.t + b.b) / 2 * r.height) - s.top) / s.height * 100).toFixed(1) });
    });
    var groups = {};
    rows.forEach(function (x) { var k = x.where + '|' + x.cls + '|' + x.view; (groups[k] = groups[k] || []).push(x.heightPct); });
    rows.forEach(function (x) { var g = groups[x.where + '|' + x.cls + '|' + x.view].slice().sort(function (a, b) { return a - b; }); var m = g[Math.floor(g.length / 2)]; x.median = m; x.flag = Math.abs(x.heightPct - m) > 3 || x.bounds === 'none' || x.bounds === 'guard' ? 'OUTLIER' : ''; });
    if (window.console && console.table) console.table(rows);
    return { rows: rows.length, outliers: rows.filter(function (x) { return x.flag; }), warned: warned };
  }
  window.KMFrameGuard = { run: pass, report: report };
})();
